/**
 * Workspace settings (CLAUDE.md 12.1).
 *
 * Present in **every** workspace, including a personal one. That is deliberate and
 * easy to get wrong: 12.1 says a sole practitioner has a letterhead and defaults too,
 * and hiding this whole section from them would mean their reports carry no
 * organisation identity at all.
 *
 * What *is* hidden in a personal workspace is the part that has no meaning there:
 * project visibility (forced private, 11.3) and the member list. `allow_self_check`
 * stays visible with its own explanation, because the reason it defaults differently
 * is a professional-responsibility point rather than a preference (2.2).
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

interface Workspace {
  id: number
  name: string
  slug: string
  kind: string
  is_personal: boolean
  locale_default: string
  default_code_id: string
  default_param_set: string
  default_project_visibility: string
  allow_self_check: boolean
  allow_share_links: boolean
  share_link_default_days: number
  role: string
  capabilities: string[]
  member_count: number
}

const selectClass = 'h-11 w-full rounded-md border border-border bg-surface px-3 text-text'

export function WorkspaceSettings() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data: workspace } = useQuery({
    queryKey: ['workspace', 'current'],
    queryFn: () => api.get<Workspace>('/workspaces/current'),
  })

  // Untouched fields stay undefined so a save sends only what changed -- a PATCH that
  // echoes every field back can silently revert someone else's concurrent edit.
  const [draft, setDraft] = useState<Partial<Workspace>>({})

  const save = useMutation({
    mutationFn: (body: Partial<Workspace>) => api.patch(`/workspaces/${workspace?.id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspace', 'current'] })
      setDraft({})
      toast({ title: t('common.saved'), variant: 'success' })
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    },
  })

  if (!workspace) return null

  // The server is the authority; this only decides whether to render the controls.
  const canEdit = workspace.capabilities.includes('workspace:manage_settings')
  const value = <K extends keyof Workspace>(key: K): Workspace[K] =>
    (draft[key] ?? workspace[key]) as Workspace[K]
  const dirty = Object.keys(draft).length > 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.label')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <Field>
          <FieldLabel>{t('common.name')}</FieldLabel>
          <FieldInput
            value={value('name')}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
        </Field>

        <Field>
          <FieldLabel>{t('common.code')}</FieldLabel>
          {/* Code names and parameter-set ids are identifiers, never translated (14.4). */}
          <select
            className={selectClass}
            disabled={!canEdit}
            value={value('default_code_id')}
            onChange={(event) => setDraft({ ...draft, default_code_id: event.target.value })}
          >
            <option value="EC2">EC2</option>
            <option value="BS8110">BS8110</option>
            <option value="ACI318">ACI318</option>
          </select>
        </Field>

        <Field>
          <FieldLabel>{t('report.param_set', { ns: 'report' })}</FieldLabel>
          <select
            className={selectClass}
            disabled={!canEdit}
            value={value('default_param_set')}
            onChange={(event) => setDraft({ ...draft, default_param_set: event.target.value })}
          >
            <option value="EC2-RECOMMENDED">EC2-RECOMMENDED</option>
            <option value="EC2-UK-NA">EC2-UK-NA</option>
            <option value="EC2-FR-NA">EC2-FR-NA</option>
          </select>
        </Field>

        {!workspace.is_personal && (
          <Field description={t('workspace.visibility_hint')}>
            <FieldLabel>{t('workspace.default_visibility')}</FieldLabel>
            <select
              className={selectClass}
              disabled={!canEdit}
              value={value('default_project_visibility')}
              onChange={(event) =>
                setDraft({ ...draft, default_project_visibility: event.target.value })
              }
            >
              <option value="private">{t('workspace.visibility.private')}</option>
              <option value="workspace">{t('workspace.visibility.workspace')}</option>
            </select>
          </Field>
        )}

        <Field description={t('workspace.self_check_hint')}>
          <div className="flex items-center gap-2">
            <input
              id="allow-self-check"
              type="checkbox"
              disabled={!canEdit}
              checked={value('allow_self_check')}
              onChange={(event) => setDraft({ ...draft, allow_self_check: event.target.checked })}
            />
            <label htmlFor="allow-self-check" className="text-sm">
              {t('workspace.allow_self_check')}
            </label>
          </div>
        </Field>

        {/* Section 12.1: external sharing is a firm-level policy question, so it
            sits with the other defaults rather than inside the share dialog where
            only the person already sharing would find it. */}
        <Field description={t('workspace.share_links_hint')}>
          <div className="flex items-center gap-2">
            <input
              id="allow-share-links"
              type="checkbox"
              disabled={!canEdit}
              checked={value('allow_share_links')}
              onChange={(event) => setDraft({ ...draft, allow_share_links: event.target.checked })}
            />
            <label htmlFor="allow-share-links" className="text-sm">
              {t('workspace.allow_share_links')}
            </label>
          </div>
        </Field>

        {value('allow_share_links') && (
          <Field description={t('workspace.share_expiry_hint')}>
            <FieldLabel>{t('workspace.share_expiry')}</FieldLabel>
            <FieldInput
              type="number"
              inputMode="numeric"
              min={1}
              max={730}
              disabled={!canEdit}
              value={String(value('share_link_default_days') ?? 30)}
              onChange={(event) =>
                setDraft({ ...draft, share_link_default_days: Number(event.target.value) })
              }
            />
          </Field>
        )}

        {canEdit && (
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {t('action.save')}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}
